// WebSocket 主从机连接模块
// 主机：连接WebSocket服务器，发送心跳，广播消息给从机
// 从机：通过广播通道接收主机消息，检测心跳超时后尝试升级为主机

import * as ls from './localStorage.js';

// ========================================================
// 配置项
// ========================================================
let enableLog = false;  // 是否启用控制台日志输出（true-开启，false-关闭）

// 日志输出函数（封装console.log，根据配置决定是否输出）
function log(...args) {
    if (enableLog) {
        console.zb(...args);
    }
}

// ========================================================
// 全局变量
// ========================================================

// 连接配置
let ws_url = 'ws://127.0.0.1:3200';      // WebSocket服务器地址
let bc_name = 'ws_link';                  // 广播通道名称

// 对象实例
let ws_object = null;                     // WebSocket连接对象
let bc_object = null;                     // 广播通道对象

// 状态管理
let my_state = 0;                         // 当前状态: 0-未知, 1-主机, 2-从机
let ws_token = '';                        // 本机令牌（仅主机持有）
let isInitialized = false;                // 是否已初始化（防重入）
let isSleeping = false;                   // 休眠状态标志（从机心跳超时时判断）

// 定时器
let time_heart = null;                    // 主机心跳定时器（10秒）
let time_check = null;                    // 从机心跳检测定时器（25秒超时）
let time_token = null;                    // 令牌更新定时器（30秒）

// 事件回调列表
let cbs = [];                             // 外部事件回调函数列表

// beforeunload事件处理
function handleBeforeUnload() {
    if (my_state === 1 && ws_token) {
        const storedToken = ls.getKeyStr('ws_token');
        if (storedToken === ws_token) {
            ls.removeKey('ws_token');
            sendBcMessage({
                type: 'master_exit',
                token: ws_token,
                timestamp: Date.now()
            });
        }
    }
}

// 页面可见性变化处理
function handleVisibilityChange() {
    if (document.hidden) {
        log('[SLEEP] 页面不可见，进入休眠状态');
        isSleeping = true;
    } else {
        log('[SLEEP] 页面可见，退出休眠状态');
        isSleeping = false;
    }
}

// ========================================================
// 初始化入口
// ========================================================

// 初始化WebSocket模块
// 检测浏览器支持 → 连接广播通道 → 判断初始角色
async function initWebSocket() {
    // 防重入：已初始化则直接返回
    if (isInitialized) {
        log('[INIT] 已初始化，跳过重复调用');
        return;
    }

    // 1. 检测浏览器支持
    if (!isWsAndBcSupported()) {
        console.error('[INIT] WebSocket服务不支持');
        return;
    }

    // 2. 连接广播通道
    let isLinked = false;
    try {
        isLinked = await linkBc() as boolean;
    } catch (error) {
        console.error('[INIT] WebSocket服务不支持');
        return;
    }

    if (!isLinked) {
        console.error('[INIT] 广播通道初始化失败');
        return;
    }

    // 标记初始化完成
    isInitialized = true;

    // 注册页面关闭事件监听
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // 注册页面可见性变化事件监听
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 3. 判断初始角色
    const storedToken = ls.getKeyStr('ws_token');
    if (parseToken(storedToken)) {
        // 检测到有效令牌，说明已有主机，成为从机
        log('[INIT] 检测到有效令牌，成为从机');
        becomeSlave();
    } else {
        // 未检测到有效令牌，尝试成为主机
        log('[INIT] 未检测到有效令牌，尝试成为主机');
        await tryBecomeMaster();
    }
}

// ========================================================
// 浏览器支持检测
// ========================================================

// 判断浏览器是否支持WebSocket和BroadcastChannel
function isWsAndBcSupported() {
    return 'WebSocket' in window && 'BroadcastChannel' in window;
}

// ========================================================
// 令牌管理
// ========================================================

// 解析令牌，判断是否过期（有效期30秒）
// token: 令牌字符串，格式为 "时间戳-随机字符"
// 返回: true-有效, false-无效或过期
function parseToken(token) {
    if (!token) return false;

    const parts = token.split('-');
    if (parts.length < 2) return false;

    const timestamp = parseInt(parts[0], 10);
    if (isNaN(timestamp)) return false;

    const now = Date.now();
    const isExpired = now - timestamp > 30000; // 30秒过期
    return !isExpired;
}

// 生成新令牌（格式：时间戳-随机字符）
function generateToken() {
    return Date.now() + '-' + Math.random().toString(36).slice(2, 7);
}

// 更新本机令牌（仅主机使用）
// 检测到有新主机时自动降级为从机
function updateToken() {
    const storedToken = ls.getKeyStr('ws_token');

    // 如果已有有效令牌且不是自己的，说明有其他主机
    if (storedToken && parseToken(storedToken) && storedToken !== ws_token) {
        log('[TOKEN] 检测到新主机令牌，自动降级为从机');
        becomeSlave();
        return;
    }

    // 生成并保存新令牌（仅保存到本地，不广播）
    ws_token = generateToken();
    ls.setKey('ws_token', ws_token);

    log('[TOKEN] 令牌已更新:', ws_token);
}

// ========================================================
// 广播通道 (BroadcastChannel)
// ========================================================

// 连接广播通道（Promise方式）
// 返回: Promise，成功resolve(true)，失败reject(false)
function linkBc() {
    return new Promise((resolve, reject) => {
        try {
            bc_object = new BroadcastChannel(bc_name);

            // 监听广播消息
            bc_object.onmessage = (event) => {
                handleBcMessage(event.data);
            };

            // 监听通道关闭
            bc_object.onclose = () => {
                log('[BC] 广播通道已关闭');
            };

            log('[BC] 广播通道连接成功:', bc_name);
            resolve(true);
        } catch (error) {
            console.error('[BC] 广播通道连接失败:', error);
            reject(false);
        }
    });
}

// 处理收到的广播消息
// data: 消息数据对象
function handleBcMessage(data) {
    if (!data || typeof data !== 'object') return;

    // 心跳消息单独处理
    if (data.type === 'heartbeat') {
        handleHeartbeat(data);
        return;
    }

    // 主机退出通知：从机收到后尝试成为主机
    if (data.type === 'master_exit') {
        // 只有从机需要处理
        if (my_state === 2) {
            log('[BC] 从机收到主机退出通知');
            // 随机延迟（0-1000ms）避免多个从机同时竞争
            const delay = Math.random() * 1000;
            log('[BC] 随机延迟', Math.round(delay), 'ms后检查令牌');
            setTimeout(() => {
                const currentToken = ls.getKeyStr('ws_token');
                if (!parseToken(currentToken)) {
                    log('[BC] 令牌已无效，尝试成为主机');
                    tryBecomeMaster();
                }
            }, delay);
        }
        return;
    }

    // 从机转发消息：主机收到后代为发送到WebSocket
    if (data.type === 'forward' && my_state === 1) {
        log('[BC] 主机收到从机转发请求:', data.data);
        if (ws_object && ws_object.readyState === WebSocket.OPEN) {
            try {
                ws_object.send(JSON.stringify(data.data));
                log('[WS] 从机消息已转发到服务器');
            } catch (error) {
                console.error('[WS] 转发消息失败:', error);
            }
        }
        return;
    }

    // 从机处理其他广播消息（仅处理主机广播的服务器消息）
    if (my_state === 2) {
        // 忽略其他从机的转发请求
        if (data.type === 'forward') {
            log('[BC] 从机忽略其他从机的转发消息');
            return;
        }
        // 处理主机广播的服务器消息
        processMessage(data);
    }
}

// 发送广播消息
// value: 消息内容对象
// 返回: true-发送成功, false-发送失败
function sendBcMessage(value) {
    if (!bc_object) {
        console.error('[BC] 广播通道未初始化');
        return false;
    }

    try {
        bc_object.postMessage(value);
        return true;
    } catch (error) {
        console.error('[BC] 消息发送失败:', error);
        return false;
    }
}

// ========================================================
// WebSocket连接
// ========================================================

// 连接WebSocket服务器
// url: 服务器地址
// 返回: Promise，成功resolve(true)，失败reject(error)
function linkWs(url) {
    return new Promise((resolve, reject) => {
        // 先断开已有连接
        if (ws_object) {
            ws_object.close();
            ws_object = null;
        }

        ws_url = url;
        ws_object = new WebSocket(url);

        // 连接成功
        ws_object.onopen = () => {
            log('[WS] 连接成功:', url);
            resolve(true);
        };

        // 收到消息
        ws_object.onmessage = (event) => {
            handleWsMessage(event.data);
        };

        // 连接关闭
        ws_object.onclose = (event) => {
            log('[WS] 连接关闭:', event.code, event.reason);
            ws_object = null;
            const storedToken = ls.getKeyStr('ws_token');
            // 主机断开后尝试重连
            if (my_state === 1) {
                const storedToken = ls.getKeyStr('ws_token');
                // 只有令牌还是自己的才尝试重连
                if (storedToken === ws_token) {
                    log('[WS] 主机连接断开，尝试重连');
                    reLinkWs();
                } else {
                    log('[WS] 令牌已变更，降级为从机');
                    becomeSlave();
                }
            }
        };

        // 连接错误
        ws_object.onerror = (error) => {
            console.error('[WS] 连接错误:', error);
            ws_object = null;
            reject(error);
        };
    });
}

/**
 * 创建新的 WebSocket 连接（裸连接）
 * 仅建立连接并绑定 onmessage，不处理关闭和重连
 * @returns {Promise<WebSocket>} 连接成功时返回 WebSocket 实例
 */
function newLinkWs() {
    return new Promise((resolve, reject) => {
        const newWs = new WebSocket(ws_url);

        newWs.onmessage = (event) => handleWsMessage(event.data);

        newWs.onopen = () => {
            log('[WS] 新连接成功');
            resolve(newWs);
        };

        newWs.onclose = (event) => {
            log('[WS] 新连接关闭:', event.code);
            reject(new Error('Connection closed before open'));
        };

        newWs.onerror = (error) => {
            console.error('[WS] 新连接错误:', error);
        };
    });
}

/**
 * 使用 newLinkWs 连接，成功后自动附加关闭处理，失败则重试最多 2 次
 * 如果最终失败，降级为从机
 * 这个函数既用于初始连接，也用于后续重连
 * @returns {Promise<boolean>} 是否成功建立连接并附加处理
 */
async function reLinkWs() {
    const maxAttempts = 2;
    for (let i = 1; i <= maxAttempts; i++) {
        log(`[WS] 连接尝试第 ${i}/${maxAttempts} 次`);
        try {
            const newWs = await newLinkWs() as WebSocket;

            // --- 成功：替换全局连接并附加关闭处理（内联逻辑）---
            ws_object = newWs;

            // 附加关闭处理（相当于原来的 attachCloseHandler）
            newWs.onclose = (event) => {
                log('[WS] 连接关闭 (主机):', event.code);
                ws_object = null;

                if (my_state === 1) {
                    const storedToken = ls.getKeyStr('ws_token');
                    if (storedToken === ws_token) {
                        log('[WS] 主机断开，启动重连');
                        reLinkWs();                   // 递归重连
                    } else {
                        log('[WS] 令牌已变更，降级为从机');
                        becomeSlave();
                    }
                }
            };

            newWs.onerror = (error) => {
                console.error('[WS] 连接错误 (主机):', error);
                // 交给 onclose 处理
            };
            // ------------------------------------------------

            log('[WS] 连接就绪');
            return true;
        } catch (err) {
            log(`[WS] 第 ${i} 次连接失败`);
            if (i < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
        }
    }

    // 所有尝试失败
    log('[WS] 连接次数用尽，降级为从机');
    becomeSlave();
    return false;
}

// 处理WebSocket消息
// data: 收到的消息数据（字符串格式）
function handleWsMessage(data) {
    try {
        const message = JSON.parse(data);
        log('[WS] 收到消息:', message);

        // 使用统一处理逻辑处理消息
        processMessage(message);

        // 主机将服务器消息广播给从机
        if (my_state === 1) {
            sendBcMessage(message);
        }
    } catch (error) {
        console.error('[WS] 消息解析失败:', error);
    }
}

// ========================================================
// 消息处理
// ========================================================

// 统一消息处理逻辑（广播消息和WebSocket消息共用）
// message: 消息对象，包含type字段
function processMessage(message) {
    // 仅处理welcome消息，其他消息触发事件回调
    if (message?.type === 'welcome') {
        log('[MSG] 欢迎消息:', message.message);
    } else {
        // 非welcome消息，直接传递原始消息
        triggerEvent(message);
    }
}

// ========================================================
// 主从角色管理
// ========================================================

// 尝试成为主机（带竞争检测）
// 流程: 1.清除心跳定时器 → 2.设置令牌 → 3.等待500ms → 4.检查令牌 → 5.成为主机
async function tryBecomeMaster() {
    // 1. 清除心跳检测定时器（避免重复触发成为主机）
    stopHeartbeatCheck();

    // 2. 生成并保存候选令牌
    const candidateToken = generateToken();
    ws_token = candidateToken;
    ls.setKey('ws_token', candidateToken);

    log('[MASTER] 候选令牌已设置:', candidateToken);

    // 3. 等待500ms让其他节点竞争
    await new Promise(resolve => setTimeout(resolve, 500));

    // 4. 检查令牌是否被其他节点更改
    const currentStoredToken = ls.getKeyStr('ws_token');
    if (currentStoredToken !== candidateToken) {
        log('[MASTER] 检测到其他节点已成为主机，放弃竞争');
        becomeSlave();
        return;
    }

    // 5. 令牌未被更改，正式成为主机
    await becomeMaster();
}

// 正式成为主机
async function becomeMaster() {
    // 设置主机状态
    my_state = 1;
    log('[MASTER] 开始成为主机...');

    // 连接WebSocket服务器
    try {
        await linkWs(ws_url);
    } catch (error) {
        console.error('[MASTER] WebSocket连接失败，降级为从机');
        becomeSlave();
        return;
    }

    // 启动主机任务
    startHeartbeat();      // 10秒心跳
    startTokenUpdate();    // 30秒更新令牌

    // 立即发送一次心跳，让从机快速感知
    sendHeartbeat();

    log('[MASTER] 成功成为主机');
}

// 成为从机
function becomeSlave() {
    // 设置从机状态
    my_state = 2;

    // 停止所有主机定时器
    stopHeartbeat();
    stopTokenUpdate();

    // 关闭WebSocket连接（如果存在）
    if (ws_object) {
        ws_object.close();
        ws_object = null;
    }

    // 启动心跳检测 15s~20s
    startHeartbeatCheck();

    log('[SLAVE] 成功成为从机');
}

// ========================================================
// 心跳机制
// ========================================================

// 发送心跳（立即发送一次）
// 主机调用：发送到WebSocket服务器 + 广播给所有从机
// 即使WebSocket断开重连中，仍会广播心跳给从机
function sendHeartbeat() {
    const heartbeat = {
        type: 'heartbeat',
        token: ws_token,
        timestamp: Date.now()
    };

    // 发送到WebSocket服务器（如果连接正常）
    if (ws_object && ws_object.readyState === WebSocket.OPEN) {
        ws_object.send(JSON.stringify(heartbeat));
    }

    // 广播给从机（无论WebSocket是否连接，都广播）
    sendBcMessage(heartbeat);

    log('[HEARTBEAT] 主机心跳已发送 - 当前时间:', new Date().toLocaleTimeString());
}

// 启动主机心跳定时器（10秒一次）
function startHeartbeat() {
    stopHeartbeat();

    time_heart = setInterval(() => {
        sendHeartbeat();
    }, 10000); // 10秒一次
}

// 停止主机心跳定时器
function stopHeartbeat() {
    if (time_heart) {
        clearInterval(time_heart);
        time_heart = null;
    }
}

// 启动从机心跳检测定时器
// timeout: 超时时间（毫秒），默认15000ms
function startHeartbeatCheck(timeout = 15000) {
    stopHeartbeatCheck();

    // 在基础时间上添加0-5秒的随机延迟
    const randomDelay = Math.random() * 5000;
    // 休眠状态下使用90秒检测间隔，否则使用默认间隔
    const baseTime = isSleeping ? 90000 : timeout;
    const totalTime = baseTime + randomDelay;

    time_check = setTimeout(() => {
        // 心跳超时，尝试成为主机
        log('[HEARTBEAT] 心跳超时，尝试成为主机');
        tryBecomeMaster();
    }, totalTime);
}

// 停止从机心跳检测定时器
function stopHeartbeatCheck() {
    if (time_check) {
        clearTimeout(time_check);
        time_check = null;
    }
}

// 处理收到的心跳消息
// 从机收到心跳后重置检测定时器
// data: 心跳消息对象
function handleHeartbeat(data) {
    // 只有从机需要处理心跳
    if (my_state !== 2) return;

    log('[HEARTBEAT] 从机收到心跳:', data.token, '- 当前时间:', new Date().toLocaleTimeString());

    // 重置心跳检测定时器
    stopHeartbeatCheck();
    startHeartbeatCheck();
}

// ========================================================
// 令牌更新定时器
// ========================================================

// 启动令牌更新定时器（30秒一次）
function startTokenUpdate() {
    stopTokenUpdate();

    time_token = setInterval(() => {
        updateToken();
    }, 30000); // 30秒更新一次
}

// 停止令牌更新定时器
function stopTokenUpdate() {
    if (time_token) {
        clearInterval(time_token);
        time_token = null;
    }
}

// ========================================================
// 事件驱动接口（内部方法）
// ========================================================

// 注册事件回调函数
// 当有新消息时，会调用此回调通知外界
// fn: 回调函数，接收事件对象 { type, time, data, status }
function onWsEvent(fn) {
    cbs.push(fn);
}

// 移除事件回调函数
function offWsEvent(fn) {
    cbs = cbs.filter(f => f !== fn);
}

// 发送消息到WebSocket服务器
// 主机：直接发送到WebSocket
// 从机：通过广播发送给主机，由主机代为发送
// message: 消息对象
// 返回: true-发送成功, false-发送失败
function sendWsMessage(message) {
    // 主机模式：直接发送到WebSocket
    if (my_state === 1) {
        if (!ws_object || ws_object.readyState !== WebSocket.OPEN) {
            console.error('[WS] WebSocket未连接');
            return false;
        }

        try {
            ws_object.send(JSON.stringify(message));
            log('[WS] 主机直接发送消息:', message);
            return true;
        } catch (error) {
            console.error('[WS] 消息发送失败:', error);
            return false;
        }
    }

    // 从机模式：通过广播发送给主机
    if (my_state === 2) {
        if (!bc_object) {
            console.error('[BC] 广播通道未初始化');
            return false;
        }

        try {
            bc_object.postMessage({
                type: 'forward',
                data: message
            });
            log('[BC] 从机通过广播转发消息:', message);
            return true;
        } catch (error) {
            console.error('[BC] 广播转发失败:', error);
            return false;
        }
    }

    console.error('[WS] 未知状态，无法发送消息');
    return false;
}

// 停止WebSocket服务
// 关闭所有连接，清除所有定时器，重置状态
// 如果是主机，会清理令牌并通知从机
function stopWebSocket() {
    log('[STOP] 正在停止WebSocket服务...');

    // 1. 如果是主机，清理令牌并通知从机
    if (my_state === 1 && ws_token) {
        const storedToken = ls.getKeyStr('ws_token');
        // 只有令牌是自己的才清理
        if (storedToken === ws_token) {
            log('[STOP] 主机退出，清理令牌并通知从机');
            // 广播通知从机主机即将退出
            sendBcMessage({
                type: 'master_exit',
                token: ws_token,
                timestamp: Date.now()
            });
            // 清除本地令牌
            ls.removeKey('ws_token');
        }
    }

    // 2. 停止所有定时器
    stopHeartbeat();
    stopHeartbeatCheck();
    stopTokenUpdate();

    // 3. 关闭WebSocket连接
    if (ws_object) {
        ws_object.close();
        ws_object = null;
        log('[WS] WebSocket连接已关闭');
    }

    // 4. 关闭广播通道
    if (bc_object) {
        bc_object.close();
        bc_object = null;
        log('[BC] 广播通道已关闭');
    }

    // 5. 移除事件监听
    window.removeEventListener('beforeunload', handleBeforeUnload);
    document.removeEventListener('visibilitychange', handleVisibilityChange);

    // 6. 重置状态变量
    my_state = 0;
    ws_token = '';
    isInitialized = false;

    log('[STOP] WebSocket服务已停止');
}

// 获取当前状态信息
// 返回: 状态对象 { role, roleName, token, isConnected }
function getWsStatus() {
    const roleNames = {
        0: '未知',
        1: '主机',
        2: '从机'
    };

    return {
        role: my_state,
        roleName: roleNames[my_state],
        token: ws_token,
        isConnected: ws_object !== null && ws_object.readyState === WebSocket.OPEN
    };
}

// ========================================================
// 统一API导出
// ========================================================

// WebSocket模块API对象
const WebSocketAPI = {
    // 初始化WebSocket服务
    init: initWebSocket,

    // 注册事件回调（收到消息时触发）
    onEvent: onWsEvent,

    // 移除事件回调
    offEvent: offWsEvent,

    // 发送消息到服务器
    sendMessage: sendWsMessage,

    // 获取当前状态
    getStatus: getWsStatus,

    // 停止WebSocket服务
    stop: stopWebSocket
};

// 默认导出统一API对象
export default WebSocketAPI;

// ========================================================
// 内部辅助方法
// ========================================================

// 触发事件通知
// data: 事件数据
function triggerEvent(data) {
    cbs.forEach(fn => fn(data));
}
